import React, { useState, useEffect, useMemo, useContext } from 'react';
import { ThemeContext } from '../App';
import { supabase } from '../supabaseClient';
import { buscarDadosZARC } from '../services/zarcService';
import './PlanejamentoSafra.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function diasAte(dataStr) {
  if (!dataStr) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = parseLocalDate(dataStr);
  return Math.ceil((alvo - hoje) / 86400000);
}

function formatarData(dataStr) {
  if (!dataStr) return '—';
  const [y, m, d] = dataStr.split('-');
  return `${d}/${m}/${y}`;
}

const ITENS_PADRAO = [
  { id: 'analise_solo', label: 'Análise de solo realizada' },
  { id: 'calcario', label: 'Calcário aplicado (se necessário)' },
  { id: 'sementes', label: 'Sementes adquiridas' },
  { id: 'insumos', label: 'Insumos adquiridos' },
  { id: 'preparo_solo', label: 'Preparo do solo concluído' },
  { id: 'maquinario', label: 'Maquinário verificado' },
];

// ─── Componente de badge ZARC ─────────────────────────────────────────────────

function BadgeZARC({ risco }) {
  if (!risco) return null;
  const config = {
    20: { label: 'Ideal', bg: '#e8f5e9', color: '#2e7d32', icon: '🟢' },
    30: { label: 'Adequado', bg: '#fff8e1', color: '#f57f17', icon: '🟡' },
    40: { label: 'Atenção', bg: '#fff3e0', color: '#e65100', icon: '🟠' },
    50: { label: 'Restrito', bg: '#ffebee', color: '#c62828', icon: '🔴' },
  };
  const c = config[risco] || config[50];
  return (
    <span className="crop-plan-zarc-badge" style={{
      background: c.bg, color: c.color,
      fontWeight: 700, fontSize: 13,
      padding: '3px 10px', borderRadius: 12
    }}>
      {c.icon} {c.label}
    </span>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function PlanejamentoSafra({ talhao, podeEditar }) {
  const { darkMode } = useContext(ThemeContext);
  const diasRestantes = diasAte(talhao.data_plantio);

  // ZARC
  const [zarc, setZarc] = useState(null);
  const [loadingZarc, setLoadingZarc] = useState(false);

  // Estimativa de produção
  const [produtividadeAlvo, setProdutividadeAlvo] = useState('');
  const producaoEstimada = useMemo(() => {
    const area = parseFloat(talhao.area);
    const prod = parseFloat(produtividadeAlvo);
    if (area > 0 && prod > 0) return (area * prod).toFixed(1);
    return null;
  }, [talhao.area, produtividadeAlvo]);

  // Checklist customável
  const itensKey = `climorax_checklist_itens_${talhao.id}`;
  const checkKey = `climorax_checklist_${talhao.id}`;
  const [itens, setItens] = useState(() => {
    try { return JSON.parse(localStorage.getItem(itensKey)) || ITENS_PADRAO; } catch { return ITENS_PADRAO; }
  });
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(checkKey)) || {}; } catch { return {}; }
  });
  const [novoItem, setNovoItem] = useState('');

  const salvarItens = (novosItens) => {
    setItens(novosItens);
    try { localStorage.setItem(itensKey, JSON.stringify(novosItens)); } catch {}
  };

  const adicionarItem = () => {
    const label = novoItem.trim();
    if (!label) return;
    const id = `custom_${Date.now()}`;
    salvarItens([...itens, { id, label }]);
    setNovoItem('');
  };

  const removerItem = (id) => {
    salvarItens(itens.filter(i => i.id !== id));
    const novo = { ...checked };
    delete novo[id];
    setChecked(novo);
    try { localStorage.setItem(checkKey, JSON.stringify(novo)); } catch {}
  };

  // Anotações de planejamento
  const notasKey = `climorax_notas_planejamento_${talhao.id}`;
  const [notas, setNotas] = useState(() => {
    try { return localStorage.getItem(notasKey) || ''; } catch { return ''; }
  });
  const salvarNotas = (valor) => {
    setNotas(valor);
    try { localStorage.setItem(notasKey, valor); } catch {}
  };

  const toggleItem = (id) => {
    if (!podeEditar) return;
    const novo = { ...checked, [id]: !checked[id] };
    setChecked(novo);
    try { localStorage.setItem(checkKey, JSON.stringify(novo)); } catch {}
  };

  const totalChecked = Object.values(checked).filter(Boolean).length;
  const progressoPct = itens.length > 0 ? Math.round((totalChecked / itens.length) * 100) : 0;

  // Buscar ZARC
  useEffect(() => {
    async function fetchZarc() {
      if (!talhao.cultura || !talhao.propriedade_id) return;
      setLoadingZarc(true);
      try {
        // Busca code_muni da propriedade
        const { data: propData } = await supabase
          .from('propriedades')
          .select('code_muni')
          .eq('id', talhao.propriedade_id)
          .single();
        if (propData?.code_muni) {
          const dados = await buscarDadosZARC(propData.code_muni, talhao.cultura);
          setZarc(dados);
        }
      } catch (e) {
        // Silencia erro ZARC
      } finally {
        setLoadingZarc(false);
      }
    }
    fetchZarc();
  }, [talhao.cultura, talhao.propriedade_id]);

  // Decêndio da data de plantio planejada
  const decendioPlantio = useMemo(() => {
    if (!talhao.data_plantio) return null;
    const d = parseLocalDate(talhao.data_plantio);
    const mes = d.getMonth(); // 0-11
    const dia = d.getDate();
    const decNoMes = dia <= 10 ? 1 : dia <= 20 ? 2 : 3;
    return mes * 3 + decNoMes; // 1-36
  }, [talhao.data_plantio]);

  const riscoZarcPlantio = useMemo(() => {
    if (!zarc || !decendioPlantio) return null;
    return zarc.decendios[decendioPlantio - 1];
  }, [zarc, decendioPlantio]);

  const cicloEstimado = useMemo(() => {
    if (!talhao.data_plantio || !talhao.data_colheita) return null;
    const plantio = parseLocalDate(talhao.data_plantio);
    const colheita = parseLocalDate(talhao.data_colheita);
    const dias = Math.ceil((colheita - plantio) / 86400000);
    return dias > 0 ? dias : null;
  }, [talhao.data_plantio, talhao.data_colheita]);

  return (
    <div className={`crop-plan-workspace${darkMode ? ' is-dark' : ''}`}>

      {/* ── Banner de countdown ── */}
      <header className="crop-plan-hero" style={{
        background: darkMode ? 'linear-gradient(135deg, #1a2a3a, #1e3a2a)' : 'linear-gradient(135deg, #e3f2fd, #f0fdf4)',
        borderRadius: 12,
        padding: '18px 20px',
        border: '2px solid #43cea2',
        textAlign: 'center'
      }}>
        <div className="crop-plan-hero-copy">
          <span>Preparação da safra</span>
          <h3>Planejamento pré-plantio</h3>
          <p>Organize as decisões, insumos e atividades necessárias antes da implantação da cultura.</p>
        </div>
        <div className="crop-plan-countdown">
          <span>Contagem regressiva</span>
          <strong>
            {diasRestantes != null
              ? diasRestantes === 0
                ? 'Hoje'
                : diasRestantes
              : '—'}
          </strong>
          <small>{diasRestantes === 0 ? 'Plantio programado para hoje' : diasRestantes != null ? 'dias para o plantio' : 'data não definida'}</small>
        </div>
        <div className="crop-plan-milestones">
          <span><small>Plantio planejado</small><strong>{formatarData(talhao.data_plantio)}</strong></span>
          <span><small>Colheita prevista</small><strong>{formatarData(talhao.data_colheita)}</strong></span>
          <span><small>Ciclo estimado</small><strong>{cicloEstimado ? `${cicloEstimado} dias` : 'Não definido'}</strong></span>
        </div>
      </header>

      <div className="crop-plan-decision-grid">

      {/* ── ZARC ── */}
      <section className="crop-plan-card crop-plan-zarc">
        <div className="crop-plan-card-heading">
          <span className="crop-plan-card-icon">🗺️</span>
          <div><small>Risco climático</small><h4>Compatibilidade ZARC</h4></div>
        </div>
        {!talhao.cultura ? (
          <div className="crop-plan-state neutral">Cadastre a cultura do talhão para verificar o ZARC.</div>
        ) : loadingZarc ? (
          <div className="crop-plan-state neutral">Consultando zoneamento...</div>
        ) : !zarc ? (
          <div className="crop-plan-state warning"><strong>Dados não disponíveis</strong><span>ZARC não encontrado para {talhao.cultura} nesta região.</span></div>
        ) : (
          <div className="crop-plan-zarc-content">
            <div className="crop-plan-zarc-risk">
              <span>Risco na data planejada</span>
              <BadgeZARC risco={riscoZarcPlantio} />
            </div>
            <div className="crop-plan-zarc-periods">
              <span><small>Períodos ideais</small><strong>{zarc.mesesIdeais || '—'}</strong></span>
              <span><small>Períodos adequados</small><strong>{zarc.mesesAdequados || '—'}</strong></span>
            </div>
            {riscoZarcPlantio > 20 && zarc.melhorPeriodo && (
              <div className="crop-plan-state success" style={{
                background: '#e8f5e9', borderRadius: 8, padding: '8px 12px',
                fontSize: 13, color: '#2e7d32', marginTop: 4
              }}>
                💡 Melhor período: <strong>{zarc.melhorPeriodo.descricao}</strong>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Estimativa de produção ── */}
      <section className="crop-plan-card crop-plan-production">
        <div className="crop-plan-card-heading">
          <span className="crop-plan-card-icon">📊</span>
          <div><small>Projeção operacional</small><h4>Estimativa de produção</h4></div>
        </div>
        <div className="crop-plan-production-form">
          <div className="crop-plan-area">
            <small>Área planejada</small><strong>{talhao.area ? `${talhao.area} ha` : '—'}</strong>
          </div>
          <label className="crop-plan-target">
            <span>Produtividade alvo</span>
            <div>
            <input
              type="number"
              min={0}
              step="any"
              value={produtividadeAlvo}
              onChange={e => setProdutividadeAlvo(e.target.value)}
              disabled={!podeEditar}
              placeholder="ex: 60"
              style={{
                width: 80, padding: '4px 8px', borderRadius: 6,
                border: `1px solid ${darkMode ? '#555' : '#b0c4de'}`, fontSize: 14,
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#e0e0e0' : '#333'
              }}
            />
            <small>sc/ha</small>
            </div>
          </label>
        </div>
        {producaoEstimada && (
          <div className="crop-plan-production-result" style={{
            marginTop: 12, background: '#e3f2fd', borderRadius: 8,
            padding: '10px 14px', fontSize: 15, fontWeight: 700, color: '#1565c0'
          }}>
            <small>Produção estimada</small><strong>{producaoEstimada}</strong><span>sacas</span>
          </div>
        )}
      </section>
      </div>

      {/* ── Checklist pré-plantio ── */}
      <section className="crop-plan-card crop-plan-checklist">
        <div className="crop-plan-checklist-heading">
          <div className="crop-plan-card-heading">
            <span className="crop-plan-card-icon">✓</span>
            <div><small>Prontidão operacional</small><h4>Checklist pré-plantio</h4><p>Marque as etapas concluídas antes da implantação.</p></div>
          </div>
          <div className="crop-plan-progress-ring" style={{ '--crop-plan-progress': `${progressoPct * 3.6}deg` }}>
            <div><strong>{progressoPct}%</strong><small>{totalChecked}/{itens.length}</small></div>
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="crop-plan-progress-track" style={{
          background: darkMode ? '#2a2a2a' : '#e3eefd', borderRadius: 8, height: 8, marginBottom: 12, overflow: 'hidden'
        }}>
          <div className="crop-plan-progress-fill" style={{
            width: `${progressoPct}%`,
            height: '100%',
            background: progressoPct === 100 ? '#43cea2' : '#1976d2',
            borderRadius: 8,
            transition: 'width 0.3s'
          }} />
        </div>

        <div className="crop-plan-checklist-items">
          {itens.map(item => (
            <div
              className={`crop-plan-check-item${checked[item.id] ? ' is-complete' : ''}`}
              key={item.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', borderRadius: 8,
                background: checked[item.id] ? '#e8f5e9' : (darkMode ? '#2a2a2a' : '#f8fafc'),
                border: `1px solid ${checked[item.id] ? '#a5d6a7' : (darkMode ? '#444' : '#e0e7ef')}`,
                transition: 'all 0.15s'
              }}
            >
              <input
                aria-label={item.label}
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={() => toggleItem(item.id)}
                disabled={!podeEditar}
                style={{ width: 18, height: 18, accentColor: '#1976d2', flexShrink: 0, cursor: podeEditar ? 'pointer' : 'default' }}
              />
              <span
                className="crop-plan-check-label"
                onClick={() => toggleItem(item.id)}
                style={{
                  flex: 1, fontSize: 14,
                  color: checked[item.id] ? '#2e7d32' : (darkMode ? '#e0e0e0' : '#333'),
                  textDecoration: checked[item.id] ? 'line-through' : 'none',
                  fontWeight: checked[item.id] ? 500 : 400,
                  cursor: podeEditar ? 'pointer' : 'default'
                }}
              >
                {item.label}
              </span>
              {podeEditar && (
                <button
                  className="crop-plan-remove-item"
                  onClick={() => removerItem(item.id)}
                  title="Remover item"
                  style={{
                    background: 'none', border: 'none', color: '#bbb',
                    cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px',
                    flexShrink: 0
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Campo para adicionar novo item */}
        {podeEditar && (
          <div className="crop-plan-add-item">
            <input
              type="text"
              value={novoItem}
              onChange={e => setNovoItem(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && adicionarItem()}
              placeholder="Adicionar item ao checklist..."
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 8,
                border: `1px solid ${darkMode ? '#555' : '#b0c4de'}`, fontSize: 14, outline: 'none',
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#e0e0e0' : '#333'
              }}
            />
            <button
              className="crop-plan-add-button"
              onClick={adicionarItem}
              disabled={!novoItem.trim()}
              style={{
                background: novoItem.trim() ? '#1976d2' : '#e0e7ef',
                color: novoItem.trim() ? '#fff' : '#aaa',
                border: 'none', borderRadius: 8,
                padding: '8px 16px', fontWeight: 600,
                fontSize: 14, cursor: novoItem.trim() ? 'pointer' : 'default',
                transition: 'all 0.15s', whiteSpace: 'nowrap'
              }}
            >
              + Adicionar
            </button>
          </div>
        )}

        {progressoPct === 100 && (
          <div className="crop-plan-ready" style={{
            marginTop: 12, background: '#e8f5e9', borderRadius: 8,
            padding: '10px 14px', textAlign: 'center',
            fontSize: 14, fontWeight: 700, color: '#2e7d32'
          }}>
            🎉 Tudo pronto para o plantio!
          </div>
        )}
      </section>

      {/* ── Anotações ── */}
      <section className="crop-plan-card crop-plan-notes">
        <div className="crop-plan-card-heading">
          <span className="crop-plan-card-icon">📝</span>
          <div><small>Memória do planejamento</small><h4>Anotações</h4><p>Registre decisões, lembretes e pontos de atenção para a safra.</p></div>
        </div>
        <textarea
          className="crop-plan-notes-field"
          value={notas}
          onChange={e => salvarNotas(e.target.value)}
          disabled={!podeEditar}
          placeholder="Registre lembretes, observações ou qualquer informação relevante para esta safra..."
          rows={4}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 8,
            border: `1px solid ${darkMode ? '#555' : '#b0c4de'}`,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
            color: darkMode ? '#e0e0e0' : '#333',
            background: podeEditar ? (darkMode ? '#2a2a2a' : '#fff') : (darkMode ? '#1a1a2e' : '#f8fafc'),
            outline: 'none'
          }}
        />
        {notas && (
          <div className="crop-plan-saved">
            ✓ Salvo automaticamente neste dispositivo
          </div>
        )}
      </section>
    </div>
  );
}
